import { createComponentStore } from '../../utils/createComponentStore';
import useProjectStore from '../ProjectCanvas/useProjectStore';

const pad2 = (n) => String(n).padStart(2, '0');

// Bidirectional maps for legacy template (uses numeric values for radio/select)
const COMPONENT_TYPE_MAP = {
  0: 'elapsed', 1: 'specific_datetime', 2: 'day_of_week', 3: 'placeholder_datetime',
};
const COMPONENT_TYPE_REVERSE = Object.fromEntries(
  Object.entries(COMPONENT_TYPE_MAP).map(([k, v]) => [v, Number(k)])
);

const INTERVAL_MAP = {
  1: 'minutes', 2: 'hours', 3: 'days', 4: 'weeks', 5: 'months', 6: 'businessDays',
};
const INTERVAL_REVERSE = Object.fromEntries(
  Object.entries(INTERVAL_MAP).map(([k, v]) => [v, Number(k)])
);

const buildComponentData = () => ({
  outputKey: 'timeDelay' + Math.floor(Math.random() * 90000000 + 10000),
  timezone: useProjectStore.getState().timezone,
  mode: 'elapsed',
});

// Java LocalDateTime: { year, month, day, hour, minute } → "YYYY-MM-DDTHH:MM"
const normalizeDateTimeLocal = (val) => {
  if (!val || typeof val === 'string') return val || '';
  if (val.year != null) {
    return `${val.year}-${pad2(val.month)}-${pad2(val.day)}T${pad2(val.hour ?? 0)}:${pad2(val.minute ?? 0)}`;
  }
  return '';
};

const { createStore: createTimeDelayStore, getStore: getTimeDelayStore, defaultStore } =
  createComponentStore({
    type: 'time_delay',
    endpoint: 'time-delay',
    registryKey: 'time-delay',
    initData: buildComponentData,

    onLoadData: (rawData) => {
      const data = { ...rawData };
      // v1 → v2 field compat mappings
      if (data.componentType != null && data.mode == null) { data.mode = data.componentType; delete data.componentType; }
      if (data.delayBy != null && data.delayAmount == null) { data.delayAmount = data.delayBy; delete data.delayBy; }
      if (data.delayInterval != null && data.delayUnit == null) { data.delayUnit = data.delayInterval; delete data.delayInterval; }
      if (data.atDatetimePlaceholder != null && data.datetimePlaceholder == null) { data.datetimePlaceholder = data.atDatetimePlaceholder; delete data.atDatetimePlaceholder; }
      if (data.day != null && data.dayOfWeek == null) { data.dayOfWeek = data.day; delete data.day; }
      if (data.outputPlaceholderKey != null && data.outputKey == null) { data.outputKey = data.outputPlaceholderKey; delete data.outputPlaceholderKey; }
      if (data.key != null && data.outputKey == null) { data.outputKey = data.key; delete data.key; }
      data.date = normalizeDateTimeLocal(data.date);
      return data;
    },

    extraState: () => ({
      position: null,
      error: false,
    }),

    extraActions: (set, get) => ({
      // Legacy template conversion helpers
      configComponentType: (num) => COMPONENT_TYPE_MAP[num] || 'UNSET',
      configComponentTypeValue: (str) => COMPONENT_TYPE_REVERSE[str] ?? 0,
      configInterval: (num) => INTERVAL_MAP[num] || '',
      configIntervalValue: (str) => INTERVAL_REVERSE[str] ?? 0,

      setData: (value, field) => {
        set((s) => {
          const data = { ...s.data };
          switch (field) {
            case 'componentType':
            case 'mode': data.mode = COMPONENT_TYPE_MAP[value] || value; break;
            case 'delayInterval':
            case 'delayUnit': data.delayUnit = INTERVAL_MAP[value] || value; break;
            case 'delayBy': data.delayAmount = value; break;
            case 'atDatetimePlaceholder': data.datetimePlaceholder = value; break;
            case 'day': data.dayOfWeek = value; break;
            default: data[field] = value;
          }
          return { data };
        });
      },

      setPlaceholder: (value) => set((s) => ({ data: { ...s.data, outputKey: value } })),
      setTimeDelayName: (name) => set({ name }),

      // Override getTemplate to include position and use structuredClone
      getTemplate: () => {
        const { componentId, name, position, data, type, lastModified } = get();
        return { componentId, name, position, data: structuredClone(data), type, lastModified };
      },
    }),
  });

export { getTimeDelayStore };
export default defaultStore;
